import {announce} from '@github-ui/aria-live'
import {
  ProofOfPresenceBody,
  type ProofOfPresencePresentation,
} from '@github-ui/pull-request-proof-of-presence-presentation'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useLocation} from '@github-ui/react-router'
import {useReturnFocus} from '@github-ui/use-return-focus'
import {Button} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {useReviewersSectionAnchorRef} from './reviewers/ReviewersSection'
import {useProofOfPresence} from './data/use-proof-of-presence'
import {SidebarSection} from './SidebarSection'

const emptyRef = {current: null}

export function ProofOfPresenceSection() {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const overviewQuery = useOverviewRouteData()

  // The proof-of-presence URL is the server-evaluated organization-scoped rollout gate, avoiding a second flag check.
  if (!overviewQuery.data?.proofOfPresenceData?.url) {
    return null
  }

  return <ProofOfPresenceSectionContent key={`${owner}/${repo}/${pr_number}`} />
}

function ProofOfPresenceSectionContent() {
  const {proofOfPresence, isError, refetch, isFetching} = useProofOfPresence()
  const {pathname, search, hash} = useLocation()
  const fallbackFocusRef = useReviewersSectionAnchorRef()
  const {containerRef} = useReturnFocus(fallbackFocusRef ?? emptyRef)
  const proofState = proofOfPresence?.state ?? null
  const previousStateRef = useRef<string | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const focusedDescendantRef = useRef<HTMLElement | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const isUnavailable = isError || isRetrying

  const setSectionRef = useCallback(
    (node: HTMLDivElement | null) => {
      sectionRef.current = node
      return containerRef(node)
    },
    [containerRef],
  )

  useEffect(() => {
    const currentState = isUnavailable ? 'unavailable' : proofState
    const previousState = previousStateRef.current
    previousStateRef.current = currentState
    if (!currentState || previousState === currentState) return

    if (currentState === 'pending') {
      announce('Proof of presence required')
    } else if (currentState === 'unavailable') {
      announce('Unable to load proof of presence. Try again later.')
    } else if (currentState === 'error') {
      announce(proofOfPresence?.message ?? 'Proof of presence unavailable')
    } else if (currentState === 'completed' && previousState) {
      announce('Proof of presence verified')
    }
  }, [isUnavailable, proofState, proofOfPresence?.message])

  useEffect(() => {
    const focusedDescendant = focusedDescendantRef.current
    if (!focusedDescendant || !sectionRef.current) return

    if (sectionRef.current.contains(focusedDescendant)) return

    const activeElement = document.activeElement
    if (activeElement !== document.body && activeElement !== document.documentElement) {
      focusedDescendantRef.current = null
      return
    }

    const target = fallbackFocusRef?.current ?? sectionRef.current
    if (document.activeElement !== target && !target.contains(document.activeElement)) {
      target.focus({preventScroll: true})
    }
    focusedDescendantRef.current = null
  }, [proofOfPresence, isUnavailable, fallbackFocusRef])

  if (!proofOfPresence && !isError && !isRetrying) return null

  const presentation: ProofOfPresencePresentation | null = isUnavailable
    ? {state: 'error', message: 'Unable to load proof of presence. Try again later.'}
    : proofOfPresence

  return (
    <div
      ref={setSectionRef}
      onFocusCapture={event => {
        const target = event.target
        if (target instanceof HTMLElement && sectionRef.current?.contains(target)) {
          focusedDescendantRef.current = target
        }
      }}
      role="group"
      aria-label="Proof of presence"
      tabIndex={-1}
      className="f6"
      data-overview-sidebar-item
      data-testid="proof-of-presence"
    >
      <SidebarSection title="Proof of presence">
        {presentation ? <ProofOfPresenceBody data={presentation} returnTo={`${pathname}${search}${hash}`} /> : null}
        {isUnavailable ? (
          <Button
            variant="link"
            inactive={isFetching || isRetrying || undefined}
            aria-disabled={isFetching || isRetrying || undefined}
            onClick={() => {
              if (isFetching || isRetrying) return
              focusedDescendantRef.current = null
              setIsRetrying(true)
              void refetch().finally(() => {
                setIsRetrying(false)
                requestAnimationFrame(() => {
                  if (document.activeElement === document.body) {
                    sectionRef.current?.focus({preventScroll: true})
                  }
                })
              })
            }}
            className="mt-2"
          >
            Retry
          </Button>
        ) : null}
      </SidebarSection>
    </div>
  )
}
